import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { list } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft, Loader2 } from "lucide-react";

const fmt = (v) => (v ? String(v).slice(0, 10) : "");
const line = (v) => v || "—";

function Cell({ label, value, className = "" }) {
  return (
    <div className={`border border-neutral-400 p-2 ${className}`}>
      <div className="text-[9px] uppercase tracking-wide text-neutral-500 font-semibold">{label}</div>
      <div className="text-sm mt-0.5">{line(value)}</div>
    </div>
  );
}

export default function WorkOrderDocument() {
  const { id, kind } = useParams();
  const nav = useNavigate();
  const [r, setR] = useState(null);
  const isBol = kind === "bol";

  useEffect(() => {
    list("requisitions").then((rows) => setR(rows.find((x) => x.id === id) || false));
  }, [id]);

  if (r === null) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (r === false) return <div className="min-h-screen grid place-items-center text-muted-foreground">Record not found.</div>;

  const carrier = r.asset_source === "Internal Asset"
    ? `${r.driver_name || "Unassigned"} · ${[r.truck_type, r.truck_unit_number].filter(Boolean).join(" #")}${r.trailer_number ? ` + ${r.trailer_type} #${r.trailer_number}` : ""}`
    : `${r.third_party_carrier || "Unassigned"}${r.third_party_contact ? ` · ${r.third_party_contact}` : ""}`;

  return (
    <div className="min-h-screen bg-neutral-100 py-8">
      <div className="max-w-3xl mx-auto mb-4 flex items-center justify-between no-print px-4">
        <Button variant="outline" size="sm" className="gap-2" onClick={() => nav("/work-orders")}><ArrowLeft className="h-4 w-4" /> Back</Button>
        <Button size="sm" className="gap-2 bg-[#0055FF] hover:bg-[#0044CC]" data-testid="doc-print-btn" onClick={() => window.print()}><Printer className="h-4 w-4" /> Print</Button>
      </div>

      <div className="print-area max-w-3xl mx-auto bg-white p-8 shadow-sm" data-testid="wo-document">
        <div className="flex items-start justify-between border-b-2 border-neutral-900 pb-4 mb-5">
          <div className="flex items-center gap-3">
            <img src="/ghost-logo.png" alt="Casper" className="h-12 w-12 rounded-md" />
            <div>
              <div className="font-display font-extrabold text-xl leading-none">GHOST TMS</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mt-1">{isBol ? "Bill of Lading" : "Work Order"}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="font-mono-data font-bold text-lg">{r.work_order_no || r.requisition_no}</div>
            <div className="text-xs text-neutral-500">Req {r.requisition_no}</div>
            <div className="text-xs text-neutral-500">Date {fmt(r.request_date) || fmt(r.estimated_start)}</div>
          </div>
        </div>

        {isBol ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-0">
              <Cell label="Ship From (Shipper)" value={`${line(r.pickup_location)}${r.pickup_site_contact ? ` · ${r.pickup_site_contact}` : ""}${r.pickup_contact_phone ? ` · ${r.pickup_contact_phone}` : ""}`} />
              <Cell label="Ship To (Consignee)" value={`${line(r.delivery_location)}${r.delivery_site_contact ? ` · ${r.delivery_site_contact}` : ""}${r.delivery_contact_phone ? ` · ${r.delivery_contact_phone}` : ""}`} />
            </div>
            <div className="grid grid-cols-3 gap-0">
              <Cell label="Carrier" value={carrier} className="col-span-2" />
              <Cell label="Client / Bill To" value={r.client_name} />
            </div>
            <div className="grid grid-cols-4 gap-0">
              <Cell label="Freight Class (NMFC)" value={r.freight_class || "50"} />
              <Cell label="Weight" value={r.weight_lbs ? `${r.weight_lbs} lb` : r.estimated_weight} />
              <Cell label="Dimensions" value={(r.length_in && r.width_in && r.height_in) ? `${r.length_in}×${r.width_in}×${r.height_in} in` : r.load_dimensions} />
              <Cell label="Pieces / Description" value={r.load_description} className="" />
            </div>
            <div className="grid grid-cols-2 gap-0">
              <Cell label="Special Instructions" value={r.special_instructions} className="col-span-2" />
            </div>
            <div className="border border-neutral-400 p-3 text-[11px] text-neutral-600 leading-relaxed">
              Received, subject to the classifications and lawfully filed tariffs in effect on the date of issue, the property described above in apparent good order, except as noted (contents and condition of contents of packages unknown).
            </div>
            {r.pod_signed_by ? (
              <div className="border-2 border-emerald-500 rounded-sm" data-testid="bol-pod-block">
                <div className="bg-emerald-500 text-white text-[10px] uppercase tracking-[0.18em] font-semibold px-3 py-1">Proof of Delivery</div>
                <div className="p-3 grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <div className="text-[9px] uppercase tracking-wide text-neutral-500 font-semibold">Received By</div>
                    <div className="text-sm font-medium">{line(r.pod_signed_by)}</div>
                    <div className="text-[11px] text-neutral-600">{r.pod_at ? new Date(r.pod_at).toLocaleString() : ""}</div>
                    {r.pod_notes && <div className="text-[11px] text-neutral-600 mt-1"><span className="font-semibold">Notes:</span> {r.pod_notes}</div>}
                    {r.pod_signature && (
                      <div className="mt-2">
                        <div className="text-[9px] uppercase tracking-wide text-neutral-500 font-semibold mb-0.5">Signature</div>
                        <img src={r.pod_signature} alt="Signature" className="h-20 border border-neutral-300 bg-white object-contain" />
                      </div>
                    )}
                  </div>
                  {r.pod_photo && (
                    <div>
                      <div className="text-[9px] uppercase tracking-wide text-neutral-500 font-semibold mb-0.5">Delivery Photo</div>
                      <img src={r.pod_photo} alt="Delivery" className="w-full max-h-40 border border-neutral-300 object-contain" data-testid="bol-pod-photo" />
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-8 pt-6">
                <div className="text-xs text-neutral-500 border-t border-neutral-400 pt-1">Shipper signature / date</div>
                <div className="text-xs text-neutral-500 border-t border-neutral-400 pt-1">Carrier / Driver signature / date</div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-0">
              <Cell label="Project" value={`${line(r.project_name)}${r.project_number ? ` (#${r.project_number})` : ""}`} />
              <Cell label="Client" value={r.client_name} />
            </div>
            <div className="grid grid-cols-2 gap-0">
              <Cell label="Pickup" value={r.pickup_location} />
              <Cell label="Delivery" value={r.delivery_location} />
            </div>
            <div className="grid grid-cols-3 gap-0">
              <Cell label="Priority" value={r.priority} />
              <Cell label="Est. Start" value={r.estimated_start ? String(r.estimated_start).slice(0, 16).replace("T", " ") : "—"} />
              <Cell label="Est. Hours" value={r.estimated_time_hrs} />
            </div>
            <div className="grid grid-cols-2 gap-0">
              <Cell label="Carrier / Equipment" value={carrier} className="col-span-2" />
            </div>
            <div className="grid grid-cols-3 gap-0">
              <Cell label="Freight Class" value={r.freight_class || "50"} />
              <Cell label="Weight" value={r.weight_lbs ? `${r.weight_lbs} lb` : r.estimated_weight} />
              <Cell label="Est. Cost" value={r.estimated_cost != null ? `$${Number(r.estimated_cost).toLocaleString()}` : "—"} />
            </div>
            <div className="grid grid-cols-1 gap-0">
              <Cell label="Scope of Work" value={r.scope_of_work || r.load_description} />
              <Cell label="Special Instructions" value={r.special_instructions} />
            </div>
            <div className="grid grid-cols-2 gap-8 pt-6">
              <div className="text-xs text-neutral-500 border-t border-neutral-400 pt-1">Dispatcher signature / date</div>
              <div className="text-xs text-neutral-500 border-t border-neutral-400 pt-1">Driver signature / date</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
